import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import USMap from "../components/licensePlate/USMap";
import StatePlateGallery from "../components/licensePlate/StatePlateGallery";
import { STATE_NAMES } from "../data/licensePlateStates.js";
import { SYM, withEllipsis } from "../utils/textSymbols";
import {
  getStateTripSpottingHistory,
  getTripLeader,
  isTripActive,
  sortTripsForDisplay,
  tripsCreditedForSpot
} from "../utils/licensePlateTripStats";
import "./LicensePlateGamePage.css";

const TABS = [
  { id: "Map", lines: ["Map"] },
  { id: "Trips", lines: ["Trips"] },
  { id: "Leaderboard", lines: ["Leader", "board"] }
];

const OVERALL_FILTER = "__overall__";

const GUEST_COLORS = ["#6b5b95", "#88b04b", "#e15b5b", "#92a8d1", "#955251", "#b565a7", "#009b77", "#dd4124"];

function guestColor(id) {
  let hash = 0;
  const str = String(id || "");
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return GUEST_COLORS[hash % GUEST_COLORS.length];
}

function getSpotLocation(timeoutMs = 8000) {
  return new Promise((resolve) => {
    if (!navigator?.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy
      }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 30000 }
    );
  });
}

function formatCoords(lat, lng) {
  if (typeof lat !== "number" || typeof lng !== "number") return null;
  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}

function mapsUrl(lat, lng) {
  if (typeof lat !== "number" || typeof lng !== "number") return null;
  return `https://www.google.com/maps?q=${lat},${lng}`;
}

function solidMemberColor(color) {
  if (!color || typeof color !== "string") return "#2d74da";
  return color.startsWith("#") ? color.slice(0, 7) : `#${color}`.slice(0, 7);
}

function memberChipStyle(color) {
  const bg = solidMemberColor(color);
  return { backgroundColor: bg, borderColor: bg, color: "#111" };
}

function memberLabelStyle(color) {
  const bg = solidMemberColor(color);
  return { backgroundColor: bg, borderColor: "rgba(0, 0, 0, 0.12)", color: "#111" };
}

function pickMostRecentStartedTrip(trips) {
  if (!trips?.length) return "";
  const sorted = [...trips].sort((a, b) => {
    const aCreated = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bCreated = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    if (bCreated !== aCreated) return bCreated - aCreated;
    return (b.startDate || "").localeCompare(a.startDate || "");
  });
  return sorted[0].id;
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function toLocalInputValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function LicensePlateGamePage({ apiBase, familyMembers, onBack }) {
  const [tab, setTab] = useState("Map");
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [selectedTripId, setSelectedTripId] = useState("");
  const [mapFilter, setMapFilter] = useState("");
  const [leaderboardMode, setLeaderboardMode] = useState("overall");

  const [pickState, setPickState] = useState(null);
  const pickModalOpenedAtRef = useRef(0);
  const PICK_MODAL_CLICK_GUARD_MS = 400;
  const [submitting, setSubmitting] = useState(false);

  const [tripName, setTripName] = useState("");
  const [tripStart, setTripStart] = useState(todayISO());
  const [tripEnd, setTripEnd] = useState(todayISO());
  const [tripParticipants, setTripParticipants] = useState([]);
  const [tripCountForOtherTrips, setTripCountForOtherTrips] = useState(true);
  const [tripCountForAllTime, setTripCountForAllTime] = useState(true);
  const [creatingTrip, setCreatingTrip] = useState(false);
  const [newTripOpen, setNewTripOpen] = useState(false);
  const [guestModalOpen, setGuestModalOpen] = useState(false);
  const [newGuestName, setNewGuestName] = useState("");
  const [savingGuest, setSavingGuest] = useState(false);
  const [menuModal, setMenuModal] = useState(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [spotDates, setSpotDates] = useState({});
  const [editTripId, setEditTripId] = useState("");
  const [editTripStart, setEditTripStart] = useState("");
  const [editTripEnd, setEditTripEnd] = useState("");
  const [celebration, setCelebration] = useState(null);
  const clearCelebration = useCallback(() => setCelebration(null), []);
  const [isDark, setIsDark] = useState(
    () => document.documentElement.getAttribute("data-theme") === "dark"
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.getAttribute("data-theme") === "dark");
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, []);

  const members = useMemo(() => {
    const list = (familyMembers || []).filter((m) => m.id && m.id !== "all" && m.name);
    const seen = new Set();
    return list.filter((m) => {
      if (seen.has(m.id)) return false;
      seen.add(m.id);
      return true;
    });
  }, [familyMembers]);

  const memberColors = useMemo(() => {
    const map = {};
    members.forEach((m) => { map[m.id] = m.color || "#2d74da"; });
    return map;
  }, [members]);

  const memberNameById = useMemo(() => {
    const map = {};
    members.forEach((m) => { map[m.id] = m.name; });
    return map;
  }, [members]);

  const guestNameById = useMemo(() => {
    const map = {};
    (game?.guests || []).forEach((g) => { map[g.id] = g.name; });
    return map;
  }, [game?.guests]);

  const participantName = useCallback((id) => (
    memberNameById[id] || guestNameById[id] || id
  ), [memberNameById, guestNameById]);

  const displayColors = useMemo(() => {
    const map = { ...memberColors };
    (game?.guests || []).forEach((g) => { map[g.id] = guestColor(g.id); });
    return map;
  }, [memberColors, game?.guests]);

  const selectedMapTrip = useMemo(() => {
    if (mapFilter === OVERALL_FILTER || !mapFilter) return null;
    return (game?.trips || []).find((t) => t.id === mapFilter) || null;
  }, [game?.trips, mapFilter]);

  const selectablePlayers = useMemo(() => {
    if (mapFilter === OVERALL_FILTER) {
      return members.map((m) => ({ id: m.id, name: m.name, color: m.color || "#2d74da" }));
    }
    const trip = selectedMapTrip;
    if (!trip) return [];
    const ids = new Set(trip.participantIds || []);
    const family = members
      .filter((m) => ids.has(m.id))
      .map((m) => ({ id: m.id, name: m.name, color: m.color || "#2d74da" }));
    const guests = (game?.guests || [])
      .filter((g) => ids.has(g.id))
      .map((g) => ({ id: g.id, name: g.name, color: guestColor(g.id) }));
    return [...family, ...guests];
  }, [mapFilter, members, selectedMapTrip, game?.guests]);

  const tripGuestCount = useMemo(() => {
    const guestIds = new Set((game?.guests || []).map((g) => g.id));
    return tripParticipants.filter((id) => guestIds.has(id)).length;
  }, [tripParticipants, game?.guests]);

  const tripForFetch = useMemo(() => {
    if (tab === "Map" && mapFilter && mapFilter !== OVERALL_FILTER) return mapFilter;
    if (leaderboardMode === "trip" && selectedTripId) return selectedTripId;
    return undefined;
  }, [tab, mapFilter, leaderboardMode, selectedTripId]);

  const fetchGame = useCallback(async (tripId) => {
    setLoading(true);
    setError(null);
    try {
      const qs = tripId ? `?tripId=${encodeURIComponent(tripId)}` : "";
      const res = await fetch(`${apiBase}/api/GetLicensePlateGame${qs}`, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setGame(data);
      if (data.trips?.length) {
        const defaultTripId = pickMostRecentStartedTrip(data.trips);
        setSelectedTripId((prev) => (
          prev && data.trips.some((t) => t.id === prev) ? prev : defaultTripId
        ));
        setMapFilter((prev) => {
          if (prev === OVERALL_FILTER) return prev;
          if (prev && data.trips.some((t) => t.id === prev)) return prev;
          return defaultTripId || OVERALL_FILTER;
        });
      } else {
        setMapFilter(OVERALL_FILTER);
      }
      const dates = {};
      (data.history || []).forEach((row) => {
        dates[row.id] = toLocalInputValue(row.spottedAt);
      });
      setSpotDates(dates);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    fetchGame(tripForFetch);
  }, [fetchGame, tripForFetch]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [tab]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [menuOpen]);

  const displayClaims = useMemo(() => {
    if (!game) return {};
    if (mapFilter !== OVERALL_FILTER && mapFilter) return game.tripClaims || {};
    return game.overallClaims || {};
  }, [game, mapFilter]);

  const pickStateClaim = pickState ? displayClaims[pickState] : null;

  const pickStateTripHistory = useMemo(() => {
    if (!pickState || !game) return [];
    const excludeTripId = mapFilter !== OVERALL_FILTER ? mapFilter : undefined;
    return getStateTripSpottingHistory(pickState, game.history, game.trips, {
      excludeTripId,
      recentTripLimit: 4
    });
  }, [pickState, game, mapFilter]);

  const openPickModal = (stateCode) => {
    pickModalOpenedAtRef.current = Date.now();
    setPickState(stateCode);
    setActionError(null);
  };

  const handlePlayerPick = (member, e) => {
    if (Date.now() - pickModalOpenedAtRef.current < PICK_MODAL_CLICK_GUARD_MS) {
      e?.preventDefault?.();
      return;
    }
    recordSpot(member);
  };

  const handleMapFilterChange = (value) => {
    setMapFilter(value);
    if (value !== OVERALL_FILTER) {
      setSelectedTripId(value);
    }
  };

  const applyGameResponse = (data) => {
    setGame(data);
    const dates = {};
    (data.history || []).forEach((row) => {
      dates[row.id] = toLocalInputValue(row.spottedAt);
    });
    setSpotDates(dates);
  };

  const recordSpot = async (member) => {
    if (!pickState || !member) return;
    setSubmitting(true);
    setActionError(null);
    try {
      const location = await getSpotLocation();
      const body = {
        stateCode: pickState,
        spottedBy: member.id,
        spottedByName: member.name,
        general: mapFilter === OVERALL_FILTER,
        tripId: mapFilter !== OVERALL_FILTER ? mapFilter : undefined
      };
      if (location) {
        body.lat = location.lat;
        body.lng = location.lng;
        if (location.accuracy != null) body.accuracy = location.accuracy;
      }
      const res = await fetch(`${apiBase}/api/RecordLicensePlateSpot`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Error ${res.status}`);
      const stateCode = pickState;
      applyGameResponse(data);
      setPickState(null);
      setCelebration({ code: stateCode, color: member.color || "#2d74da" });
    } catch (err) {
      setActionError(err.message || String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateTrip = async (e) => {
    e.preventDefault();
    if (!tripName.trim() || tripParticipants.length === 0) return;
    setCreatingTrip(true);
    setActionError(null);
    try {
      const res = await fetch(`${apiBase}/api/CreateLicensePlateTrip`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: tripName.trim(),
          startDate: tripStart,
          endDate: tripEnd,
          participantIds: tripParticipants,
          countForAllTime: tripCountForAllTime,
          countForOtherTrips: tripCountForOtherTrips
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Error ${res.status}`);
      applyGameResponse(data);
      const newTripId = data.selectedTripId || data.trips?.[data.trips.length - 1]?.id || "";
      setSelectedTripId(newTripId);
      if (newTripId) setMapFilter(newTripId);
      setNewTripOpen(false);
      setGuestModalOpen(false);
      setTripName("");
      setTripParticipants([]);
      setTripCountForAllTime(true);
      setTripCountForOtherTrips(true);
      setTripStart(todayISO());
      setTripEnd(todayISO());
    } catch (err) {
      setActionError(err.message || String(err));
    } finally {
      setCreatingTrip(false);
    }
  };

  const openNewTripModal = () => {
    setActionError(null);
    setTripName("");
    setTripStart(todayISO());
    setTripEnd(todayISO());
    setTripParticipants([]);
    setTripCountForAllTime(true);
    setTripCountForOtherTrips(true);
    setNewGuestName("");
    setGuestModalOpen(false);
    setNewTripOpen(true);
  };

  const handleAddGuest = async (e) => {
    e?.preventDefault();
    const trimmed = newGuestName.trim();
    if (!trimmed) return;
    setSavingGuest(true);
    setActionError(null);
    try {
      const res = await fetch(`${apiBase}/api/ManageLicensePlateGuests`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "add", name: trimmed })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Error ${res.status}`);
      applyGameResponse(data);
      const added = (data.guests || []).find(
        (g) => g.name.toLowerCase() === trimmed.toLowerCase()
      );
      if (added) {
        setTripParticipants((prev) => (prev.includes(added.id) ? prev : [...prev, added.id]));
      }
      setNewGuestName("");
    } catch (err) {
      setActionError(err.message || String(err));
    } finally {
      setSavingGuest(false);
    }
  };

  const handleRemoveGuest = async (guestId) => {
    if (!window.confirm("Remove this guest from the saved list? They stay on past trips.")) return;
    setSavingGuest(true);
    setActionError(null);
    try {
      const res = await fetch(`${apiBase}/api/ManageLicensePlateGuests`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "remove", guestId })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Error ${res.status}`);
      applyGameResponse(data);
      setTripParticipants((prev) => prev.filter((id) => id !== guestId));
    } catch (err) {
      setActionError(err.message || String(err));
    } finally {
      setSavingGuest(false);
    }
  };

  const toggleGuestParticipant = (guestId) => {
    setTripParticipants((prev) =>
      prev.includes(guestId) ? prev.filter((id) => id !== guestId) : [...prev, guestId]
    );
  };

  const openMenuModal = (modal) => {
    setMenuOpen(false);
    setMenuModal(modal);
  };

  const tripLabelForHistory = (row) => {
    if (row.general) return "All-time only";
    const credited = tripsCreditedForSpot(game?.history, row, game?.trips);
    const countsAllTime = row.sourceTripId
      ? (game?.trips || []).find((t) => t.id === row.sourceTripId)?.countForAllTime !== false
      : true;
    const parts = [];
    if (countsAllTime) parts.push("All-time");
    if (credited.length) parts.push(...credited.map((t) => t.name));
    if (!parts.length) return "Trip only";
    return parts.join(", ");
  };

  const handleDeleteSpot = async (spotId) => {
    if (!window.confirm("Remove this spotting? Scores will recalculate.")) return;
    setSubmitting(true);
    setActionError(null);
    try {
      const res = await fetch(`${apiBase}/api/DeleteLicensePlateSpot`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ spotId, tripIdForResponse: tripForFetch })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Error ${res.status}`);
      applyGameResponse(data);
    } catch (err) {
      setActionError(err.message || String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveSpotDate = async (spotId) => {
    setSubmitting(true);
    setActionError(null);
    try {
      const res = await fetch(`${apiBase}/api/UpdateLicensePlateSpot`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          spotId,
          spottedAt: spotDates[spotId],
          tripIdForResponse: tripForFetch
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Error ${res.status}`);
      applyGameResponse(data);
    } catch (err) {
      setActionError(err.message || String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveTripDates = async (e) => {
    e.preventDefault();
    if (!editTripId || !editTripStart || !editTripEnd) return;
    setSubmitting(true);
    setActionError(null);
    try {
      const res = await fetch(`${apiBase}/api/UpdateLicensePlateTrip`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tripId: editTripId,
          startDate: editTripStart,
          endDate: editTripEnd
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Error ${res.status}`);
      applyGameResponse(data);
    } catch (err) {
      setActionError(err.message || String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const selectTripForEdit = (tripId) => {
    setEditTripId(tripId);
    const trip = (game?.trips || []).find((t) => t.id === tripId);
    if (trip) {
      setEditTripStart(trip.startDate || "");
      setEditTripEnd(trip.endDate || "");
    }
  };

  const toggleParticipant = (id) => {
    setTripParticipants((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const leaderboard = leaderboardMode === "trip" && selectedTripId
    ? (game?.tripLeaderboard || [])
    : (game?.overallLeaderboard || []);

  const sortedTrips = useMemo(() => sortTripsForDisplay(game?.trips), [game?.trips]);
  const tripOptions = game?.trips || [];

  return (
    <div className={`lp-page ${tab === "Map" ? "lp-page-map" : ""}`}>
      <header className="lp-header">
        <button type="button" className="lp-back-btn" onClick={onBack}>
          <span className="lp-back-arrow" aria-hidden="true">{SYM.arrowLeft}</span>
          <span className="lp-back-label">Games</span>
        </button>
        <h1 className="lp-title">License Plate</h1>
        <div className="lp-header-menu" ref={menuRef}>
          <button
            type="button"
            className="lp-menu-btn"
            aria-label="Menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
          >
            {SYM.menu}
          </button>
          {menuOpen && (
            <div className="lp-menu-panel" role="menu">
              <button type="button" className="lp-menu-item" role="menuitem" onClick={() => openMenuModal("spots")}>
                Edit spottings
              </button>
              <button type="button" className="lp-menu-item" role="menuitem" onClick={() => openMenuModal("trips")}>
                Edit trip dates
              </button>
              <button type="button" className="lp-menu-item" role="menuitem" onClick={() => openMenuModal("history")}>
                History
              </button>
            </div>
          )}
        </div>
      </header>

      <nav className="lp-tabs" aria-label="Game sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`lp-tab-btn ${tab === t.id ? "active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            <span className="lp-tab-label">
              {t.lines.map((line) => (
                <span key={line} className="lp-tab-line">{line}</span>
              ))}
            </span>
          </button>
        ))}
      </nav>

      {loading && <p className="lp-status">{withEllipsis("Loading")}</p>}
      {error && <p className="lp-error">{error}</p>}
      {actionError && <p className="lp-error">{actionError}</p>}

      {!loading && !error && tab === "Map" && (
        <section className="lp-map-section">
          <div className="lp-map-controls lp-map-controls-single">
            <select
              className="lp-select lp-map-filter"
              value={mapFilter || OVERALL_FILTER}
              onChange={(e) => handleMapFilterChange(e.target.value)}
              aria-label="Map view"
            >
              <option value={OVERALL_FILTER}>All-time</option>
              {tripOptions.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <USMap
            claims={displayClaims}
            memberColors={displayColors}
            onStateClick={openPickModal}
            celebration={celebration}
            onCelebrationComplete={clearCelebration}
            dark={isDark}
          />
        </section>
      )}

      {!loading && !error && tab === "Trips" && (
        <section className="lp-section lp-trips-section">
          <div className="lp-trips-header">
            <h2 className="lp-section-title">Trips</h2>
            <button type="button" className="lp-primary-btn lp-new-trip-btn" onClick={openNewTripModal}>
              New Trip
            </button>
          </div>
          {sortedTrips.length === 0 && <p className="lp-empty">No trips yet. Create one to start competing!</p>}
          <ul className="lp-trip-list">
            {sortedTrips.map((t) => {
              const active = isTripActive(t);
              const leader = getTripLeader(game?.history, t, game?.trips);
              const leaderColor = leader ? memberColors[leader.spottedBy] : null;
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    className={`lp-trip-card ${selectedTripId === t.id ? "selected" : ""}`}
                    onClick={() => {
                      setSelectedTripId(t.id);
                      setMapFilter(t.id);
                      setTab("Map");
                    }}
                  >
                    <div className="lp-trip-card-top">
                      <strong className="lp-trip-card-name">{t.name}</strong>
                      {active ? (
                        <span className="lp-badge lp-badge-active">Active</span>
                      ) : (
                        <span className="lp-badge lp-badge-ended">Ended</span>
                      )}
                    </div>
                    <span className="lp-trip-dates">{t.startDate} {SYM.enDash} {t.endDate}</span>
                    {leader ? (
                      <span
                        className="lp-trip-leader"
                        style={memberLabelStyle(leaderColor)}
                      >
                        {active ? "Leader" : "Winner"}: {leader.spottedByName || participantName(leader.spottedBy)} ({leader.score})
                      </span>
                    ) : (
                      <span className="lp-trip-leader lp-trip-no-scores">No scores yet</span>
                    )}
                    <span className="lp-trip-participants">
                      {t.participantIds?.map((id) => participantName(id)).join(", ")}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {!loading && !error && tab === "Leaderboard" && (
        <section className="lp-section">
          <div className="lp-map-controls">
            <button
              type="button"
              className={`lp-mode-btn ${leaderboardMode === "overall" ? "active" : ""}`}
              onClick={() => setLeaderboardMode("overall")}
            >
              All-time
            </button>
            <button
              type="button"
              className={`lp-mode-btn ${leaderboardMode === "trip" ? "active" : ""}`}
              onClick={() => setLeaderboardMode("trip")}
            >
              Trip
            </button>
            {leaderboardMode === "trip" && (
              <select
                className="lp-select"
                value={selectedTripId}
                onChange={(e) => setSelectedTripId(e.target.value)}
                aria-label="Select trip for leaderboard"
              >
                <option value="">{withEllipsis("Select trip")}</option>
                {tripOptions.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            )}
          </div>
          {leaderboard.length === 0 && <p className="lp-empty">No scores yet</p>}
          <ol className="lp-leaderboard">
            {leaderboard.map((entry, i) => (
              <li key={entry.spottedBy} className="lp-leader-row">
                <span className="lp-rank">{i + 1}</span>
                <span className="lp-player" style={memberLabelStyle(displayColors[entry.spottedBy])}>
                  {entry.spottedByName || participantName(entry.spottedBy)}
                </span>
                <span className="lp-score">{entry.score}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      {newTripOpen && (
        <div className="lp-modal-backdrop" onClick={() => !creatingTrip && setNewTripOpen(false)}>
          <div className="lp-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="lp-new-trip-title">
            <h2 id="lp-new-trip-title" className="lp-modal-title">New Trip</h2>
            <form className="lp-trip-form" onSubmit={handleCreateTrip}>
              <label className="lp-label">
                Name
                <input className="lp-input" value={tripName} onChange={(e) => setTripName(e.target.value)} required />
              </label>
              <div className="lp-row">
                <label className="lp-label">
                  Start
                  <input type="date" className="lp-input" value={tripStart} onChange={(e) => setTripStart(e.target.value)} required />
                </label>
                <label className="lp-label">
                  End
                  <input type="date" className="lp-input" value={tripEnd} onChange={(e) => setTripEnd(e.target.value)} required />
                </label>
              </div>
              <fieldset className="lp-fieldset lp-trip-count-fieldset">
                <legend>Count for</legend>
                <label className="lp-check-label">
                  <input
                    type="checkbox"
                    checked={tripCountForOtherTrips}
                    onChange={(e) => setTripCountForOtherTrips(e.target.checked)}
                  />
                  Other trips
                </label>
                <label className="lp-check-label">
                  <input
                    type="checkbox"
                    checked={tripCountForAllTime}
                    onChange={(e) => setTripCountForAllTime(e.target.checked)}
                  />
                  All time
                </label>
              </fieldset>
              <fieldset className="lp-fieldset">
                <legend>Family participants</legend>
                {members.map((m) => (
                  <label key={m.id} className="lp-check-label">
                    <input
                      type="checkbox"
                      checked={tripParticipants.includes(m.id)}
                      onChange={() => toggleParticipant(m.id)}
                    />
                    <span className="lp-member-name" style={memberChipStyle(memberColors[m.id])}>{m.name}</span>
                  </label>
                ))}
              </fieldset>
              <div className="lp-guest-trip-row">
                <button
                  type="button"
                  className="lp-secondary-btn"
                  onClick={() => setGuestModalOpen(true)}
                >
                  {tripGuestCount > 0 ? `Guests (${tripGuestCount})` : "Add guests"}
                </button>
                {tripGuestCount > 0 && (
                  <span className="lp-guest-trip-summary">
                    {(game?.guests || [])
                      .filter((g) => tripParticipants.includes(g.id))
                      .map((g) => g.name)
                      .join(", ")}
                  </span>
                )}
              </div>
              <div className="lp-modal-actions">
                <button type="submit" className="lp-primary-btn" disabled={creatingTrip}>
                  {creatingTrip ? withEllipsis("Creating") : "Create Trip"}
                </button>
                <button type="button" className="lp-cancel-btn" onClick={() => setNewTripOpen(false)} disabled={creatingTrip}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {guestModalOpen && (
        <div className="lp-modal-backdrop lp-modal-backdrop-top" onClick={() => !savingGuest && setGuestModalOpen(false)}>
          <div className="lp-modal lp-guest-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="lp-guest-modal-title">
            <h2 id="lp-guest-modal-title" className="lp-modal-title">Trip guests</h2>
            <p className="lp-modal-sub">Add guests to your saved list and choose who is playing this trip.</p>
            <form className="lp-guest-add-form" onSubmit={handleAddGuest}>
              <label className="lp-label">
                New guest
                <div className="lp-guest-add-row">
                  <input
                    className="lp-input"
                    value={newGuestName}
                    onChange={(e) => setNewGuestName(e.target.value)}
                    placeholder="Name"
                    disabled={savingGuest}
                  />
                  <button type="submit" className="lp-secondary-btn" disabled={savingGuest || !newGuestName.trim()}>
                    {savingGuest ? withEllipsis("Adding") : "Add"}
                  </button>
                </div>
              </label>
            </form>
            {(game?.guests || []).length === 0 && (
              <p className="lp-empty">No saved guests yet. Add one above.</p>
            )}
            <ul className="lp-guest-list">
              {(game?.guests || []).map((g) => (
                <li key={g.id} className="lp-guest-list-item">
                  <label className="lp-check-label lp-guest-check">
                    <input
                      type="checkbox"
                      checked={tripParticipants.includes(g.id)}
                      onChange={() => toggleGuestParticipant(g.id)}
                      disabled={savingGuest}
                    />
                    <span className="lp-member-name" style={memberChipStyle(guestColor(g.id))}>{g.name}</span>
                  </label>
                  <button
                    type="button"
                    className="lp-danger-btn lp-guest-remove-btn"
                    disabled={savingGuest}
                    onClick={() => handleRemoveGuest(g.id)}
                    aria-label={`Remove ${g.name} from saved guests`}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            <button type="button" className="lp-primary-btn" onClick={() => setGuestModalOpen(false)} disabled={savingGuest}>
              Done
            </button>
          </div>
        </div>
      )}

      {menuModal === "spots" && (
        <div className="lp-modal-backdrop" onClick={() => !submitting && setMenuModal(null)}>
          <div className="lp-modal lp-menu-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h2 className="lp-modal-title">Edit spottings</h2>
            {(game?.history || []).length === 0 && <p className="lp-empty">No spottings yet</p>}
            <ul className="lp-correction-list">
              {(game?.history || []).map((row) => (
                <li key={row.id} className="lp-correction-item">
                  <div className="lp-correction-label">
                    <strong>{row.stateCode}</strong>
                    <span>{row.spottedByName || participantName(row.spottedBy)}</span>
                  </div>
                  <input
                    type="datetime-local"
                    className="lp-input"
                    value={spotDates[row.id] || ""}
                    onChange={(e) => setSpotDates((prev) => ({ ...prev, [row.id]: e.target.value }))}
                  />
                  <div className="lp-correction-actions">
                    <button type="button" className="lp-secondary-btn" disabled={submitting} onClick={() => handleSaveSpotDate(row.id)}>
                      Save date
                    </button>
                    <button type="button" className="lp-danger-btn" disabled={submitting} onClick={() => handleDeleteSpot(row.id)}>
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            <button type="button" className="lp-cancel-btn" onClick={() => setMenuModal(null)} disabled={submitting}>
              Close
            </button>
          </div>
        </div>
      )}

      {menuModal === "trips" && (
        <div className="lp-modal-backdrop" onClick={() => !submitting && setMenuModal(null)}>
          <div className="lp-modal lp-menu-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h2 className="lp-modal-title">Edit trip dates</h2>
            <form className="lp-trip-edit-form" onSubmit={handleSaveTripDates}>
              <select
                className="lp-select"
                value={editTripId}
                onChange={(e) => selectTripForEdit(e.target.value)}
                aria-label="Trip to edit"
              >
                <option value="">{withEllipsis("Select trip")}</option>
                {tripOptions.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              {editTripId && (
                <>
                  <div className="lp-row">
                    <label className="lp-label">
                      Start
                      <input type="date" className="lp-input" value={editTripStart} onChange={(e) => setEditTripStart(e.target.value)} required />
                    </label>
                    <label className="lp-label">
                      End
                      <input type="date" className="lp-input" value={editTripEnd} onChange={(e) => setEditTripEnd(e.target.value)} required />
                    </label>
                  </div>
                  <button type="submit" className="lp-primary-btn" disabled={submitting}>Save trip dates</button>
                </>
              )}
            </form>
            <button type="button" className="lp-cancel-btn" onClick={() => setMenuModal(null)} disabled={submitting}>
              Close
            </button>
          </div>
        </div>
      )}

      {menuModal === "history" && (
        <div className="lp-modal-backdrop" onClick={() => setMenuModal(null)}>
          <div className="lp-modal lp-menu-modal lp-history-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h2 className="lp-modal-title">History</h2>
            <div className="lp-history-wrap">
              <table className="lp-history-table">
                <thead>
                  <tr>
                    <th>State</th>
                    <th>Player</th>
                    <th>Counts for</th>
                    <th>When</th>
                    <th>Location</th>
                  </tr>
                </thead>
                <tbody>
                  {(game?.history || []).map((row) => (
                    <tr key={row.id}>
                      <td>{row.stateCode} {SYM.enDash} {STATE_NAMES[row.stateCode] || row.stateCode}</td>
                      <td>{row.spottedByName || participantName(row.spottedBy)}</td>
                      <td>{tripLabelForHistory(row)}</td>
                      <td>{row.spottedAt ? new Date(row.spottedAt).toLocaleString() : SYM.emDash}</td>
                      <td>
                        {formatCoords(row.lat, row.lng) ? (
                          <a className="lp-location-link" href={mapsUrl(row.lat, row.lng)} target="_blank" rel="noopener noreferrer">
                            {formatCoords(row.lat, row.lng)}
                          </a>
                        ) : SYM.emDash}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button type="button" className="lp-cancel-btn" onClick={() => setMenuModal(null)}>
              Close
            </button>
          </div>
        </div>
      )}

      {pickState && pickStateClaim && (
        <div className="lp-modal-backdrop" onClick={() => setPickState(null)}>
          <div
            className="lp-modal lp-modal-spotted"
            style={{ borderColor: displayColors[pickStateClaim.spottedBy] || "#2d74da" }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h2 className="lp-modal-title">{STATE_NAMES[pickState] || pickState}</h2>
            <StatePlateGallery stateCode={pickState} />
            <div
              className="lp-spot-claim"
              style={memberLabelStyle(displayColors[pickStateClaim.spottedBy])}
            >
              <span className="lp-spot-claim-name">
                {pickStateClaim.spottedByName || participantName(pickStateClaim.spottedBy)}
              </span>
              {pickStateClaim.spottedAt && (
                <span className="lp-spot-claim-when">
                  {new Date(pickStateClaim.spottedAt).toLocaleString()}
                </span>
              )}
              {mapFilter !== OVERALL_FILTER && selectedMapTrip && (
                <span className="lp-spot-claim-context">{selectedMapTrip.name}</span>
              )}
              {mapFilter === OVERALL_FILTER && (
                <span className="lp-spot-claim-context">All-time</span>
              )}
            </div>
            {pickStateTripHistory.length > 0 && (
              <div className="lp-spot-history">
                <h3 className="lp-spot-history-title">Previous trips</h3>
                <ul className="lp-spot-history-list">
                  {pickStateTripHistory.map(({ trip, claim }) => (
                    <li
                      key={trip.id}
                      className="lp-spot-history-item"
                      style={memberLabelStyle(displayColors[claim.spottedBy])}
                    >
                      <strong>{trip.name}</strong>
                      <span>{claim.spottedByName || participantName(claim.spottedBy)}</span>
                      {claim.spottedAt && (
                        <span className="lp-spot-history-when">
                          {new Date(claim.spottedAt).toLocaleDateString()}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <button type="button" className="lp-cancel-btn" onClick={() => setPickState(null)}>
              Close
            </button>
          </div>
        </div>
      )}

      {pickState && !pickStateClaim && (
        <div className="lp-modal-backdrop" onClick={() => !submitting && setPickState(null)}>
          <div className="lp-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h2 className="lp-modal-title">{STATE_NAMES[pickState] || pickState}</h2>
            <StatePlateGallery stateCode={pickState} />
            <p className="lp-modal-sub">Who spotted it?</p>
            {selectablePlayers.length === 0 && (
              <p className="lp-empty">No players for this trip. Add family or guests in trip setup.</p>
            )}
            <div className="lp-player-grid">
              {selectablePlayers.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className="lp-player-btn"
                  style={memberChipStyle(m.color)}
                  disabled={submitting}
                  onClick={(e) => handlePlayerPick(m, e)}
                >
                  {m.name}
                </button>
              ))}
            </div>
            <button type="button" className="lp-cancel-btn" onClick={() => setPickState(null)} disabled={submitting}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}